import ErrorMessage from '@app/components/atoms/ErrorMessage/ErrorMessage'
import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { ADMIN_ROUTE } from '@app/constant/RouteConstant'
import { toOptionValues } from '@app/libs/cast'
import { toTextFieldDateFormat } from '@app/libs/format'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import BasicInformationSection from './BasicInformationSection'
import CodeSection from './CodeSection'
import POSSection from './POSSection'
import TermAndConditionSection from './TermAndConditionSection'
import { voucherSchema } from './VoucherSchema'

const VoucherEdit = ({ voucher, categories, pos, posVoucher, tags }) => {
  const { t } = useTranslation()

  const initialValues = {
    ...voucher,
    merchant: voucher.merchant.name,
    expired_date: toTextFieldDateFormat(voucher.expired_date),
    merchantId: voucher.merchant.id,
    limit_max: voucher.limit_max || '',
    limit_min: voucher.limit_min || '',
    tags: toOptionValues(voucher.tags),
  }

  const data = {
    tags,
  }

  const onSubmit = (data) => {
    Inertia.put(route(ADMIN_ROUTE.UPDATE_VOUCHER, { id: voucher.id }), data)
  }

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(voucherSchema),
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Section title={t('VOUCHER.BASIC_INFORMATION')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <BasicInformationSection
                  categories={categories}
                  control={control}
                  data={data}
                  errors={errors}
                  getValues={getValues}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.CODES')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <CodeSection
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  register={register}
                  setValue={setValue}
                  voucherType={voucher.type}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.TERM_AND_CONDITION')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <TermAndConditionSection
                  control={control}
                  getValues={getValues}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.POS')}>
            <Grid container>
              <Grid item lg={9} md={12} sm={12} xs={12}>
                <POSSection
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  merchantId={getValues('merchantId')}
                  pos={pos}
                  posVoucher={posVoucher}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.IMAGE')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('VOUCHER.SLIDER')}>
                      <Controller
                        control={control}
                        name="images"
                        render={({ field }) => (
                          <InputUpload
                            {...field}
                            multiple
                            buttonName={t('VOUCHER.UPLOAD_IMAGE')}
                            clearErrors={clearErrors}
                            handleImageChange={(files) => setValue('images', files)}
                            id="images"
                            name="images"
                            ref={field.ref}
                            setError={setError}
                            title={t('VOUCHER.UPLOAD_IMAGE')}
                            value={getValues('images')}
                          />
                        )}
                      />
                      {errors.images && <ErrorMessage variant="caption">{errors.images?.message}</ErrorMessage>}
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item sx={{ marginBottom: '20px' }} xs="auto">
          <Button color="primary" type="submit" variant="contained">
            {t('BUTTON.SAVE')}
          </Button>
        </Grid>

        <Grid item sx={{ marginBottom: '20px' }} xs="auto">
          <Button color="inherit" component={InertiaLink} href={route('admin.vouchers.index')} variant="contained">
            {t('BUTTON.CANCEL')}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default VoucherEdit
